/**
 * config/colors.ts — Colors of ALL 3D scene elements.
 *
 * Boundary rule (SPEC §6, PLAN §1.2): `config/` contains every constant —
 * colors never hardcoded into components. Central palette (PLAN §2.8), referred
 * to the old 3d (`#b8c2cc` steel, `#fcefc7` wood) and to the viewer colors.
 *
 *Maintenance rule: if a color changes, it ONLY changes here.*/

/**
 * Scene palette. Each entry is commented with the role of the element.
 * `as const` guarantees literal types (no magic strings in components).*/
export const COLORS = {
  /** HEADER beam — tan/arancio traslucido (tema chiaro, rif. screenshot 24-09-2026).*/
  headerBeam: "#e8b878",
  /** JOIST beam — stessa famiglia tan, tono leggermente piu' chiaro per distinguerla.*/
  joistBeam: "#f0c896",
  /** Bottom of the procedural wood texture of the beams.*/
  woodBase: "#e5ddc9",
  /** Procedural wood texture grain of beams.*/
  woodGrain: "#a9854b",
  /** Separation of slats in procedural wood texture.*/
  woodLamella: "#d7c2a5",
  /** JOIST-side connector lock — blue (reference to old 3d male/joist).*/
  connectorJoist: "#2563eb",
  /** HEADER-side connector lock — red (reference to old 3d female/header).*/
  connectorHeader: "#dc2626",
  /** Lockstop bearing plate (28-09-2026) — green, distinct from both connector sides and from fastener colors.*/
  lockstop: "#16a34a",
  /** STANDARD fastener — amber (visible on gray wood).*/
  fastener: "#f59e0b",
  /** INCLINED fastener — purple (distinguished for calibration).*/
  inclinedFastener: "#8b5cf6",
  /**
   * Fastener colori PER PRODOTTO, non per ruolo: viti dello stesso item hanno
   * lo stesso colore anche se stanno su lati diversi, viti di item diversi ne
   * hanno uno ciascuna. Tre voci bastano: i ruoli sono al massimo tre (header,
   * joist, inclinata), quindi i prodotti distinti non possono essere di piu'.*/
  fastenerByProduct: ["#f59e0b", "#8b5cf6", "#22d3ee"],
  /**
   * Reference grid (X-Z plane at y=0) — grigio-blu, righe nitide sul piano
   * pieno `gridFloor` (il "foglio").*/
  grid: "#b6bfc9",
  /**
   * Piano pieno sotto le linee della griglia ("il foglio di carta",
   * 24-09-2026): bianco come `background`, il perimetro del foglio lo
   * segna `gridBorder`.*/
  gridFloor: "#ffffff",
  /**
   * Bordo del foglio (perimetro del piano griglia, richiesta utente
   * 24-09-2026: "aggiungerei un bordo... per dare quella sensazione di
   * foglio di carta") — pieno (non diluito come le righe interne), un po'
   * piu' scuro di `grid` per segnare chiaramente il perimetro.*/
  gridBorder: "#94a3b0",
  /** Debug connector plane (parallel to Y-Z at y=h) — light blue.*/
  connectorPlane: "#0ea5e9",
  /** Debugging notch/cut — orange.*/
  notchDebug: "#f97316",
  /** Corners/edge lines of all boxes (beams and fallback locks) — dark slate.*/
  edge: "#334155",
  /**
   * Spigoli dei lock veri (GLB) — piu' scuro di `edge`: i GLB portano i propri
   * materiali, tutti dello stesso grigio, quindi senza un contorno marcato
   * l'incastro header/joist e quello fra lock1 e lock2 si perdono.*/
  connectorEdge: "#0f172a",
  /** Fastener axis line in debug (Fastener AxisDebug) — yellow.*/
  fastenerAxis: "#facc15",
  /**
   * Linea tratteggiata guida nella vista esplosa (richiesta utente
   * 24-09-2026): dal centro del foro alla vite esplosa, per mostrare
   * l'allineamento. Grigio scuro neutro, non si confonde con le quote (blu)
   * ne' coi colori delle viti (per prodotto).*/
  explodeGuide: "#475569",
  /** Canvas Background — bianco, come la vista 3D del Deck Designer.*/
  background: "#ffffff",
  /**
   * Linea di quota (dimension line) — dark slate (tema chiaro, come un
   * disegno tecnico su carta bianca; era bianco quando lo sfondo era scuro).*/
  quoteDimensionLine: "#1e293b",
  /** Linee di estensione (witness lines) — grigio medio, secondarie alla linea di quota.*/
  quoteExtensionLine: "#64748b",
  /** Frecce di terminazione delle quote lineari — stesso tono della linea di quota.*/
  quoteArrow: "#1e293b",
  /** Quote angolari (arco + frecce) — ciano scuro, leggibile sul tema chiaro e distinto dalle lineari.*/
  quoteAngular: "#0e7490",
  /** Testo dell'etichetta di quota (drei Html).*/
  quoteLabelText: "#0f172a",
  /**
   * Sfondo dell'etichetta di quota (drei Html) — grigio molto chiaro (non
   * bianco puro: col canvas ora bianco, un chip bianco si distinguerebbe
   * solo grazie al bordo).*/
  // Esadecimale opaco: THREE.Color ignora l'alpha di rgba() e a ogni etichetta
  // stampava un avviso con ~100 righe di stack (0.97 non era mai applicato).
  quoteLabelBackground: "#f1f4f7",
  /**
   * Frecce dei carichi applicati (28-09-2026, richiesta utente: "aggiungere
   * le forze applicate sul connettore"). Un solo colore per tutte e 4 (non
   * uno per asse): sono la STESSA categoria concettuale (carichi applicati,
   * gia' mostrati in ResultsSection.tsx), un colore condiviso le raggruppa
   * visivamente e le distingue dal blu-grigio delle quote tecniche. Rosso
   * acceso: convenzione universale "forza/carico" nei disegni tecnici (rif.
   * immagine di riferimento utente).*/
  forceArrow: "#dc2626",
  /** Testo dell'etichetta di forza (drei Html) — stesso rosso della freccia, leggibile sul chip chiaro.*/
  forceLabelText: "#991b1b",
  /** Sfondo dell'etichetta di forza — stesso trattamento delle quote (chip chiaro sul canvas chiaro).*/
  forceLabelBackground: "#fef2f2",
} as const;

export type SceneColorKey = keyof typeof COLORS;
